import { Controller } from "@hotwired/stimulus"

// Búsqueda en vivo del listado de cotizaciones (rediseño de Laura, tarjeta sHoxYJcd).
// El filtrado sigue siendo server-side: al teclear reenviamos el mismo formulario
// GET dentro del turbo-frame `content`, así que solo se repinta el listado y la URL
// avanza sola (`data-turbo-action="advance"` del frame). Sin JS el formulario sigue
// siendo un GET normal: pulsar Enter filtra igual que antes, y los crawlers ven la
// misma página server-rendered. Esto es progressive enhancement, no un requisito.
//
// El formulario vive DENTRO del frame que recargamos, así que cada respuesta
// destruye y recrea este controlador. Todo lo que tiene que sobrevivir al swap
// (foco, caret, texto tecleado mientras volaba la petición, último término
// enviado) se guarda a nivel de módulo, no en la instancia.
const estado = { restaurar: false, valor: null, inicio: 0, fin: 0, enviado: null }

// 300 ms: `filtros_controller` usa 1000 ms porque debouncea el comparador de
// productos (formulario pesado y multi-filtro). Aquí Laura pide que "sugiera
// mientras buscas", y por encima de ~350 ms se percibe como que hay que esperar.
// Por debajo de ~250 ms una palabra corriente dispara una consulta por letra.
const ESPERA_MS = 300

// El listado tiene miles de filas y cada envío es un ILIKE sobre nombre + ticker
// + ticker_api. Con una sola letra el resultado no acota nada (devuelve media
// tabla) y sí cuesta la consulta, así que esperamos al segundo carácter. El campo
// vacío es la excepción: hay que enviarlo para restaurar el listado sin filtrar.
const MINIMO_CARACTERES = 2

export default class extends Controller {
  static targets = ["input", "limpiar"]

  connect() {
    // Turbo va en modo opt-in en esta app: `packs/hotwired.js` desactiva Drive y
    // además marca `data-turbo="false"` en fase de captura a todo formulario
    // dentro de un turbo-frame que no declare lo contrario. Sin estos atributos
    // cada pulsación sería una recarga completa. Se ponen desde JS, como en
    // `filtros_controller`, para que el HTML servido siga siendo un GET normal
    // cuando no hay JS.
    this.element.setAttribute("data-turbo", "true")
    this.element.setAttribute("data-turbo-frame", "content")

    // El término renderizado es el que ya está aplicado en servidor: sirve de
    // base para no reenviar una búsqueda idéntica a la que estamos viendo.
    if (estado.enviado === null) estado.enviado = this.termino
    this.restaurarEscritura()
    this.sincronizarLimpiar()
  }

  disconnect() {
    clearTimeout(this.temporizador)
  }

  buscar() {
    this.recordarEscritura()
    this.sincronizarLimpiar()
    clearTimeout(this.temporizador)

    // Flechas, modificadores o espacios de sobra no cambian el término: no hay
    // nada nuevo que pintar y nos ahorramos una consulta por pulsación.
    if (this.termino === estado.enviado) return

    this.temporizador = setTimeout(() => this.enviar(), ESPERA_MS)
  }

  // La "X" es un enlace normal y así se queda: su href ya apunta al listado sin
  // `q` conservando región/índice/orden, y navega por el mismo frame. No lo
  // interceptamos (un envío del formulario dejaría un `q=` vacío colgando de la
  // URL); solo dejamos anotado el estado para recuperar el foco tras el repintado.
  limpiar() {
    clearTimeout(this.temporizador)
    estado.enviado = ""
    estado.valor = ""
    estado.inicio = 0
    estado.fin = 0
    estado.restaurar = true
  }

  enviar() {
    const termino = this.termino
    if (termino.length > 0 && termino.length < MINIMO_CARACTERES) return

    estado.enviado = termino
    // Solo devolvemos el foco si el usuario lo tenía puesto en el buscador: si
    // navegó con los tabs de región no queremos robárselo.
    estado.restaurar = document.activeElement === this.inputTarget
    this.recordarEscritura()
    this.element.requestSubmit()
  }

  recordarEscritura() {
    estado.valor = this.inputTarget.value
    estado.inicio = this.inputTarget.selectionStart
    estado.fin = this.inputTarget.selectionEnd
  }

  // Tras el swap del frame el input es un nodo nuevo con el `q` de la petición.
  // Si el usuario siguió tecleando mientras volaba, su texto manda: lo devolvemos
  // al campo, recolocamos el caret y reprogramamos el envío del término real.
  restaurarEscritura() {
    if (!estado.restaurar) return

    estado.restaurar = false
    const input = this.inputTarget
    if (estado.valor !== null && estado.valor !== input.value) input.value = estado.valor
    input.focus()
    input.setSelectionRange(estado.inicio, estado.fin)
    if (this.termino !== estado.enviado) this.buscar()
  }

  // La "X" viene siempre en el HTML, oculta con `hidden` mientras el servidor no
  // tenga `q` aplicado. Aquí la descubrimos en cuanto hay algo escrito, sin
  // esperar al mínimo de caracteres ni al repintado: si no, un solo carácter
  // dejaba texto en el campo y ninguna cruz visible.
  sincronizarLimpiar() {
    this.limpiarTarget.hidden = this.termino.length === 0
  }

  get termino() {
    return this.inputTarget.value.trim()
  }
}
